import { useEffect } from "react";

import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";

import { getUserCacheInvalidation, getUserCacheInvalidationAdmin } from "services";

import { getEnvironment } from "utils";

import { GET_CACHE_INVALIDATION } from "consts";

const env = getEnvironment();

export const useCacheInvalidation = (enabled: boolean, userId?: string) => {
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: [GET_CACHE_INVALIDATION, userId],
    queryFn: queryFn(userId),
    enabled: enabled && !!userId,
    refetchInterval: 60000, // every 60 seconds
  });

  // Detect cache invalidations
  useEffect(() => {
    if (!data?.lastInvalidation) return;
    const cache = queryClient.getQueryCache();

    // For each query in the cache check if it is older than the cache invalidation
    cache.getAll().forEach((query) => {
      if (query.state.dataUpdatedAt < moment(data?.lastInvalidation).valueOf()) {
        query.invalidate();
      }
    });
  }, [data, queryClient]);

  useEffect(() => {
    data?.scoped?.forEach(({ queryKey, invalidatedAt }) => {
      queryClient.invalidateQueries({
        queryKey: [queryKey],
        predicate: (query) => query.state.dataUpdatedAt < moment(invalidatedAt).valueOf(),
      });
    });
  }, [data, queryClient]);
};

const queryFn = (userId?: string) => {
  if (env?.admin && !userId) {
    return skipToken;
  }

  if (env?.admin && userId) {
    return () => getUserCacheInvalidationAdmin({ userId });
  }

  return () => getUserCacheInvalidation();
};
